import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import axios from 'axios';
import debounce from 'lodash/debounce';
import {
    ApiEndpoints,
    Defaults,
    SearchParamNames,
    SearchTypeNames,
    SortDirections,
} from '../../../../../api/searchApiConstants';
import { getApiAxiosInstance } from '../../../../../utils/network';
import PopupV2 from '../../../../Popup/PopupV2';
import styles from '../../collegeProspectTab.scss';

const SCHOOL_SORT_FIELD = 'activeRoster.power6.power6Rating';
const SEARCH_TOP = 10;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

const GENDER_PARAM_MALE = 'M';
const GENDER_PARAM_FEMALE = 'F';

const TESTID_PREFIX = 'modal.add-college';

const resolveGenderParam = (gender) => {
    if (typeof gender !== 'string') {
        return null;
    }
    const normalized = gender.trim().toUpperCase();
    if (normalized === 'M' || normalized === 'MALE') {
        return GENDER_PARAM_MALE;
    }
    if (normalized === 'F' || normalized === 'FEMALE') {
        return GENDER_PARAM_FEMALE;
    }
    return null;
};

const buildSearchParams = (query, genderParam) => {
    const params = new URLSearchParams();
    params.append(SearchParamNames.QUERY, query);
    params.append(SearchParamNames.TOP, SEARCH_TOP);
    params.append(SearchParamNames.SKIP, Defaults.DEFAULT_SKIP);
    params.append(SearchParamNames.SCHOOL_CLUB_SEARCH, Defaults.SCHOOL_CLUB_SEARCH_VALUE);
    params.append(SearchParamNames.SORT, `${SCHOOL_SORT_FIELD}:${SortDirections.DESC}`);
    if (genderParam) {
        params.append(SearchParamNames.GENDER, genderParam);
    }
    return params;
};

const AddCollegeModal = ({ isOpen, onClose, onAddCollege }) => {
    const userGender = useSelector((state) => state.auth?.user?.gender);
    const genderParam = resolveGenderParam(userGender);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);

    const cancelRef = useRef(null);
    const modalContentRef = useRef(null);

    const fetchResults = useMemo(() => debounce(async (rawQuery, genderParamForRequest) => {
        if (cancelRef.current) {
            cancelRef.current();
        }
        const source = axios.CancelToken.source();
        cancelRef.current = source.cancel;

        try {
            const response = await getApiAxiosInstance().get(
                `${ApiEndpoints.SEARCH_BASE}/${SearchTypeNames.COLLEGES}`,
                {
                    params: buildSearchParams(rawQuery, genderParamForRequest),
                    cancelToken: source.token,
                },
            );
            setResults(response.data?.hits || []);
            setIsLoading(false);
            setHasSearched(true);
        } catch (err) {
            if (axios.isCancel(err)) {
                return;
            }
            setResults([]);
            setIsLoading(false);
            setHasSearched(true);
        }
    }, SEARCH_DEBOUNCE_MS), []);

    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setResults([]);
            setIsLoading(false);
            setHasSearched(false);
            setSelectedSchool(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleOutsideClick = (event) => {
            if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const handleQueryChange = (event) => {
        const next = event.target.value;
        setQuery(next);
        setSelectedSchool(null);
        if (next.trim().length >= MIN_QUERY_LENGTH) {
            setIsLoading(true);
            fetchResults(next.trim(), genderParam);
        } else {
            fetchResults.cancel();
            setResults([]);
            setIsLoading(false);
            setHasSearched(false);
        }
    };

    const handleSelectSchool = (hit) => {
        const school = hit.source || hit;
        setSelectedSchool(school);
        setQuery(school.name || school.displayName || '');
        setResults([]);
        setHasSearched(false);
    };

    const handleConfirm = () => {
        onAddCollege(selectedSchool);
    };

    const showDropdown = query.trim().length >= MIN_QUERY_LENGTH && !selectedSchool;

    return (
        <PopupV2
            onCloseClick={onClose}
            config={{
                size: 'medium',
                customHeader: () => null,
            }}
        >
            <div
                ref={modalContentRef}
                className={styles.addCollegeModal}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div
                    className={styles.modalTitle}
                    data-testid={`${TESTID_PREFIX}.title`}
                >
                    Add College
                </div>
                <div
                    className={styles.modalContent}
                    data-testid={`${TESTID_PREFIX}.content`}
                >
                    <div className={styles.collegeSearchWrapper}>
                        <input
                            type="text"
                            className={`form-control ${styles.collegeSearchInput}`}
                            placeholder="Search by school name"
                            value={query}
                            onChange={handleQueryChange}
                            data-testid={`${TESTID_PREFIX}.search-input`}
                        />
                        {showDropdown && (
                            <div
                                className={styles.collegeSearchDropdown}
                                data-testid={`${TESTID_PREFIX}.search-dropdown`}
                            >
                                {isLoading && (
                                    <div
                                        className={styles.collegeSearchLoading}
                                        data-testid={`${TESTID_PREFIX}.search-loading`}
                                    >
                                        <i className="fas fa-circle-notch fa-spin" />
                                        <span>Loading...</span>
                                    </div>
                                )}
                                {!isLoading && hasSearched && results.length === 0 && (
                                    <div
                                        className={styles.collegeSearchNoMatches}
                                        data-testid={`${TESTID_PREFIX}.search-no-matches`}
                                    >
                                        No Matches Found
                                    </div>
                                )}
                                {!isLoading && results.length > 0 && (
                                    <div
                                        className={styles.collegeSearchResults}
                                        data-testid={`${TESTID_PREFIX}.search-results`}
                                    >
                                        {results.map((hit, index) => {
                                            const source = hit.source || hit;
                                            const schoolId = source.id || index;
                                            const displayName = source.name || source.displayName || '';
                                            const locationDisplay = source.location?.display || '';
                                            return (
                                                <button
                                                    type="button"
                                                    key={`school-${schoolId}`}
                                                    className={styles.collegeSearchResultItem}
                                                    onClick={() => handleSelectSchool(hit)}
                                                    data-testid={`${TESTID_PREFIX}.search-results.item-${schoolId}`}
                                                >
                                                    <span
                                                        className={styles.collegeSearchResultName}
                                                        data-testid={`${TESTID_PREFIX}.search-results.item-${schoolId}.name`}
                                                    >
                                                        {displayName}
                                                    </span>
                                                    {locationDisplay && (
                                                        <span
                                                            className={styles.collegeSearchResultLocation}
                                                            data-testid={`${TESTID_PREFIX}.search-results.item-${schoolId}.location`}
                                                        >
                                                            {locationDisplay}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        className={styles.modalActions}
                        data-testid={`${TESTID_PREFIX}.actions`}
                    >
                        <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={onClose}
                            data-testid={`${TESTID_PREFIX}.cancel-btn`}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleConfirm}
                            disabled={!selectedSchool}
                            data-testid={`${TESTID_PREFIX}.confirm-btn`}
                        >
                            Add College
                        </button>
                    </div>
                </div>
            </div>
        </PopupV2>
    );
};

AddCollegeModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onAddCollege: PropTypes.func,
};

AddCollegeModal.defaultProps = {
    isOpen: false,
    onAddCollege: () => {},
};

export default AddCollegeModal;
